import * as Sentry from '@sentry/react';
import byteSize from 'byte-size';
import {
  format,
  formatDistanceToNowStrict,
  formatDuration,
  fromUnixTime,
  intervalToDuration,
  millisecondsToSeconds,
} from 'date-fns';
import { floor, round, startCase } from 'lodash-es';
import millify from 'millify';
import { useRouter } from 'next/router';
import numeral from 'numeral';
import { formatEther } from 'viem';
import { getCDNUrl } from '@soundxyz/utils';
import {
  chainIds,
  getChainName,
  resolveUsernamePublicAddress,
  SONG_SLOT_MAX_LENGTH,
} from '@soundxyz/utils/const';
import { SortOrder } from '@graphql/reactQuery';
import type { SplitType } from '@utils/constants';
import {
  AnalyticsPageCategories,
  DAY,
  HOUR,
  MINUTE,
  MONTH,
  SHELF_RELEASE_DATE_FORMAT,
  SPLITS_DECIMALS,
  SPOTIFY_BASE_URL,
  UNKNOWN_CHAIN_ID,
  UPLOAD_STEP_NAMES_OPEN_UP_SOUND,
  WEEK,
  YEAR,
} from '@utils/constants';
import { webappUriPath } from './url';

/**************************************************
                  GLOBAL HELPERS
 *************************************************/

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-463/test-for-formatusd
export const formatUSD = (num: number, removeFraction?: boolean) => {
  const fractionDigits = removeFraction ? 0 : 2;

  if (num > 999999999) {
    return '$' + numeral(num).format('0,0.000a').replace('.00', ' ');
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits,
  }).format(num);
};

export const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const getOrdinalSuffix = (num: number) => {
  num = Math.round(num);

  if (Math.floor(num / 10) % 10 === 1) {
    return 'th';
  }

  switch (num % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

const regexStringFormat = "[a-zA-Z0-9!'@#$%&();,._-]+";

export const getAnalyticsPageCategoryFromRoute = ({
  url,
  initialLand,
}: {
  url: string;
  initialLand: boolean;
}) => {
  if (url.match('^/$') || url.match('^/\\?$') || url.match(`^/\\?${regexStringFormat}`)) {
    return AnalyticsPageCategories.LANDINGV2;
  } else if (url.match('^/explore$')) {
    return AnalyticsPageCategories.EXPLORE;
  } else if (url.match('^/feed$')) {
    return AnalyticsPageCategories.ACTIVITY_FEED;
  } else if (url.match('^/notifications$')) {
    return AnalyticsPageCategories.NOTIFICATIONS;
  } else if (url.match('^/dashboard/profile')) {
    return AnalyticsPageCategories.EDIT_PROFILE;
  } else if (url.match('^/dashboard/releases$')) {
    return AnalyticsPageCategories.ARTIST_RELEASES_DASHBOARD;
  } else if (url.match('^/secret/dashboard/upload') || url.match('^/dashboard/upload')) {
    if (url.includes('/step/1')) {
      return AnalyticsPageCategories.UPLOAD_RELEASE + ' - Release Info';
    } else if (url.includes('/step/2')) {
      return AnalyticsPageCategories.UPLOAD_RELEASE + ' - Splits';
    } else if (url.includes('/step/3')) {
      return AnalyticsPageCategories.UPLOAD_RELEASE + ' - Drop Configuration';
    } else if (url.includes('/step/4')) {
      return AnalyticsPageCategories.UPLOAD_RELEASE + ' - Review & Upload';
    } else {
      return AnalyticsPageCategories.UPLOAD_RELEASE;
    }
  } else if (url.match(`^/user/${regexStringFormat}/featured/edit$`)) {
    return AnalyticsPageCategories.EDIT_PROFILE_FEATURED_SHELVES;
  } else if (url.match(`^/user/${regexStringFormat}/liked-sounds$`)) {
    return AnalyticsPageCategories.LIKED_SOUNDS;
  } else if (url.match('^/playlist/new$')) {
    return AnalyticsPageCategories.CREATE_SHELF;
  } else if (url.match(`^/playlist/${regexStringFormat}/edit$`)) {
    return AnalyticsPageCategories.EDIT_SHELF;
  } else if (url.match('^/latest$')) {
    return AnalyticsPageCategories.LATEST;
  } else if (url.match('^/charts/viral$')) {
    return AnalyticsPageCategories.VIRAL_CHARTS;
  } else if (url.match(/^\/charts\/top\/(\w+)/)) {
    return AnalyticsPageCategories.TOP_CHARTS;
  } else if (url.match('^/trending$') && !initialLand) {
    return AnalyticsPageCategories.TRENDING;
  } else if (url.match('^/trending') && initialLand) {
    return AnalyticsPageCategories.TRENDING;
  } else if (url.match(`^/playlist/${regexStringFormat}`)) {
    return AnalyticsPageCategories.PLAYLISTS;
  } else if (url.match(`^/artist-application`)) {
    return AnalyticsPageCategories.ARTIST_APPLICATION;
  } else if (
    url.match(`^/user/${regexStringFormat}/playlists`) ||
    url.match(`^/${regexStringFormat}/playlists`)
  ) {
    return AnalyticsPageCategories.PLAYLISTS;
  } else if (
    url.match(`^/user/${regexStringFormat}/collection`) ||
    url.match(`^/${regexStringFormat}/collection`)
  ) {
    return AnalyticsPageCategories.COLLECTION;
  } else if (url.match(`^/${regexStringFormat}/collectors`)) {
    return AnalyticsPageCategories.COLLECTORS;
  } else if (url.match(`^/user/${regexStringFormat}/artists-backed`)) {
    return AnalyticsPageCategories.ARTISTS_BACKED;
  } else if (url.match(`^/${regexStringFormat}/releases`)) {
    return AnalyticsPageCategories.ARTIST_RELEASES;
  } else if (
    url.match(`^/${regexStringFormat}/appears-on`) ||
    url.match(`^/user/${regexStringFormat}/appears-on`)
  ) {
    return AnalyticsPageCategories.APPEARS_ON;
  } else if (url.match(`^/${regexStringFormat}/activity`)) {
    return AnalyticsPageCategories.ARTIST_ACTIVITY;
  } else if (url.match(`^/user/${regexStringFormat}/activity`)) {
    return AnalyticsPageCategories.COLLECTOR_ACTIVITY;
  } else if (
    url.match(`^/${regexStringFormat}$`) ||
    url.match(`^/${regexStringFormat}\\?${regexStringFormat}`)
  ) {
    return AnalyticsPageCategories.ARTIST;
  } else if (url.match(`^/user/${regexStringFormat}$`)) {
    return AnalyticsPageCategories.COLLECTOR;
  } else if (
    url.match(`^/${regexStringFormat}/${regexStringFormat}$`) ||
    url.match(`^/${regexStringFormat}/${regexStringFormat}\\?${regexStringFormat}`)
  ) {
    return AnalyticsPageCategories.SONG;
  } else if (url.match(`^/${regexStringFormat}/${regexStringFormat}/presale$`)) {
    return AnalyticsPageCategories.PRESALE;
  } else if (url.match(`^/user/verify-notification-email`)) {
    return AnalyticsPageCategories.VERIFY_NOTIFICATION_EMAIL;
  } else {
    return null;
  }
};

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-465/tests-for-timeago
export function timeAgo({
  date,
  removeAppendedText,
}: {
  date: Date;
  removeAppendedText?: boolean;
}) {
  const secondsAgo = millisecondsToSeconds(+new Date() - +date);
  let divisor = 1;
  let unit = null;

  if (secondsAgo < MINUTE) {
    if (removeAppendedText) {
      return secondsAgo + ' secs';
    }

    return secondsAgo + ' secs ago';
  } else if (secondsAgo < HOUR) {
    [divisor, unit] = [MINUTE, 'min'];
  } else if (secondsAgo < DAY) {
    [divisor, unit] = [HOUR, 'hour'];
  } else if (secondsAgo < WEEK) {
    [divisor, unit] = [DAY, 'day'];
  } else if (secondsAgo < MONTH) {
    [divisor, unit] = [WEEK, 'week'];
  } else if (secondsAgo < YEAR) {
    [divisor, unit] = [MONTH, 'month'];
  } else if (secondsAgo > YEAR) {
    [divisor, unit] = [YEAR, 'year'];
  }

  const count = Math.floor(secondsAgo / divisor);
  const timeAgo = `${count} ${unit}${count > 1 ? 's' : ''}`;

  if (removeAppendedText) {
    return timeAgo;
  }

  return `${timeAgo} ago`;
}

export function formatShelfReleaseDateAdded({ date }: { date: Date }) {
  const secondsAgo = millisecondsToSeconds(+new Date() - +date);
  let divisor = 1;
  let unit = null;

  if (secondsAgo < MINUTE) {
    return '< 1m ago';
  } else if (secondsAgo < MONTH) {
    if (secondsAgo < HOUR) {
      [divisor, unit] = [MINUTE, 'm'];
    } else if (secondsAgo < DAY) {
      [divisor, unit] = [HOUR, 'h'];
    } else if (secondsAgo < WEEK) {
      [divisor, unit] = [DAY, 'd'];
    } else if (secondsAgo < MONTH) {
      [divisor, unit] = [WEEK, 'w'];
    }

    const count = Math.floor(secondsAgo / divisor);
    const timeAgo = `${count}${unit}`;

    return `${timeAgo} ago`;
  } else {
    return format(date, SHELF_RELEASE_DATE_FORMAT);
  }
}

/**************************************************
                  CHAIN HELPERS
 *************************************************/

export function etherscanPrefix(chainId: number) {
  let prefix = '';

  switch (chainId) {
    case chainIds.MAINNET:
      prefix = '';
      break;
    case chainIds.GOERLI:
      prefix = 'goerli.';
      break;
    case chainIds.OPTIMISM:
      prefix = 'optimistic.';
      break;
    case chainIds.OPTIMISM_GOERLI:
      prefix = 'goerli-optimism.';
      break;
    default:
      if (chainId !== UNKNOWN_CHAIN_ID) {
        Sentry.captureException(`Unknown chainId: ${chainId}`);
      }
      break;
  }

  return prefix;
}

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-467/test-for-etherscan-formatting-utils
export function etherscanForTransaction({
  chainId,
  transactionHash,
}: {
  transactionHash: string;
  chainId: number;
}) {
  return `https://${etherscanPrefix(chainId)}etherscan.io/tx/${transactionHash}` as const;
}

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-467/test-for-etherscan-formatting-utils
export function etherscanForContract({
  contractAddress,
  chainId,
}: {
  contractAddress: string;
  chainId: number;
}) {
  return `https://${etherscanPrefix(chainId)}etherscan.io/address/${contractAddress}` as const;
}

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-467/test-for-etherscan-formatting-utils
export function etherscanForAddress({ address, chainId }: { address: string; chainId: number }) {
  return `https://${etherscanPrefix(chainId)}etherscan.io/address/${address}` as const;
}

/**************************************************
                  MISC HELPERS
 *************************************************/

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-469/tests-for-url-validators
export function validSpotifyUrl(value: string) {
  return value.startsWith(SPOTIFY_BASE_URL);
}

// Validates image is above minimum dimensions
export function validateImageDimensions(file: File, requiredMinH = 0, requiredMinW = 0) {
  return new Promise((resolve: (respose: boolean) => void) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const { height, width } = img;
      if (height < requiredMinH || width < requiredMinW) {
        resolve(false);
      }
      resolve(true);
    };
  });
}

export function validateImageAspectRatio(file: File, ratio: number) {
  return new Promise((resolve: (respose: boolean) => void) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      const { height, width } = img;
      if (width / height !== ratio) {
        resolve(false);
      }
      resolve(true);
    };
  });
}

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-470/tests-for-formattimeduration
export function formatTimeDuration({ startTime, endTime }: { startTime: number; endTime: number }) {
  const duration = intervalToDuration({
    start: new Date(startTime),
    end: new Date(endTime),
  });

  const formattedDuration = formatDuration(duration, {
    delimiter: ', ',
    format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
  });

  return formattedDuration;
}

export function formatTimeDurationFromUnix({
  startTime,
  endTime,
}: {
  startTime: number;
  endTime: number;
}) {
  const duration = intervalToDuration({
    start: fromUnixTime(startTime),
    end: fromUnixTime(endTime),
  });

  const formattedDuration = formatDuration(duration, {
    delimiter: ', ',
    format: ['years', 'months', 'weeks', 'days', 'hours', 'minutes'],
  });

  return formattedDuration;
}

// Missing Test
// TODO: https://linear.app/sound/issue/ENG-471/test-for-stepidtotypename
export const stepIdToTypename = (step: number | number[]) => {
  switch (step) {
    case 1:
      return UPLOAD_STEP_NAMES_OPEN_UP_SOUND.RELEASE_INFO;
    case 2:
      return UPLOAD_STEP_NAMES_OPEN_UP_SOUND.SPLITS;
    case 3:
      return UPLOAD_STEP_NAMES_OPEN_UP_SOUND.DROP_CONFIGURATION;
    case 4:
      return UPLOAD_STEP_NAMES_OPEN_UP_SOUND.REVIEW_AND_DEPLOY;
    default:
      throw new Error(`Unknown stepId: ${step}`);
  }
};

export const formatDateFromTime = ({ time }: { time: number }) => {
  return format(fromUnixTime(time).getTime(), 'MMMM dd, yyyy');
};

export const getCommentPosition = (args: {
  availableWidth: number;
  avatarSize: number;
  commentTimestamp: number;
  trackDuration: number;
}): number => {
  const { availableWidth, avatarSize, commentTimestamp, trackDuration } = args;

  const widthOfSec = (availableWidth - avatarSize) / trackDuration;
  const position = widthOfSec * commentTimestamp;

  const minimumPossiblePosition = 0;
  const maximumPossiblePosition = availableWidth - avatarSize;

  return Math.min(Math.max(position, minimumPossiblePosition), maximumPossiblePosition) || 0;
};

// Throws type error if non-never type is passed to it
// eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-empty-function
export function exhaustiveCheck(_param: never) {}

const SECRET_ROUTE_PREFIX = '/secret';

export const useMaybeGetSecretUrl = (url: string) => {
  const { pathname } = useRouter();
  if (pathname.indexOf(SECRET_ROUTE_PREFIX) === 0) return `${SECRET_ROUTE_PREFIX}${url}`;
  return url;
};

export const getUrlPathForTwitter = ({
  type,
  subType,
  url,
}: {
  type: 'drop' | 'playlist' | 'viral' | 'top';
  subType?: string;
  url?: string;
}) => {
  const link = url || window.location.href;
  let message;

  switch (type) {
    case 'top':
      message = `Check%20out%20the%20${type}%20100%20${subType}%20on%20%40soundxyz_`;
      break;
    case 'viral':
      message = `Check%20out%20the%20${type}%20100%20sounds%20on%20%40soundxyz_`;
      break;
    default:
      message = `Check%20out%20this%20${type}%20on%20%40soundxyz_`;
  }

  return `https://twitter.com/intent/tweet?text=${message}%0A%0A${link}`;
};

export const getSocialShareMessage = ({
  type,
  subType,
  url,
}: {
  type: 'drop' | 'playlist' | 'viral' | 'top';
  subType?: string;
  url?: string;
}) => {
  const link = url || window.location.href;
  let message;
  switch (type) {
    case 'top':
      message = `Check%20out%20the%20${type}%20100%20${subType}%20on%20%40soundxyz_`;
      break;
    case 'viral':
      message = `Check%20out%20the%20${type}%20100%20sounds%20on%20%40soundxyz_`;
      break;
    default:
      message = `Check%20out%20this%20${type}%20on%20%40soundxyz_`;
  }
  return `${message}%0A%0A${link}`;
};

// credit: https://stackoverflow.com/a/7376645
export function hasDuplicates<T>(array: Array<T>): boolean {
  return new Set(array).size !== array.length;
}

// When we introduced open editions, we backfilled all releases to have 100 song slots
// Therefore, we can assume that all songs have 100 slots
export const getSongSlotByAudioPosition = (args: {
  position: number;
  trackDuration: number;
}): number => {
  const { position, trackDuration } = args;

  const halfSlotSize = trackDuration / SONG_SLOT_MAX_LENGTH / 2;

  const slot = Math.floor((position + halfSlotSize) / (trackDuration / SONG_SLOT_MAX_LENGTH) - 1);

  return Math.min(Math.max(slot, 0), SONG_SLOT_MAX_LENGTH - 1) || 0;
};

export const getTotalETHPrice = ({
  price,
  selectedQuantity,
}: {
  price: bigint;
  selectedQuantity: number;
}) => {
  const totalPrice = price * BigInt(selectedQuantity);
  return formatWeiToEthForDisplay({ weiValue: totalPrice.toString() });
};

export const getActivityTimestamp = (timestamp: number | Date, addSuffix?: boolean) => {
  const distance = formatDistanceToNowStrict(timestamp, { roundingMethod: 'floor', addSuffix });
  const [number, unit, suffix] = distance.split(' ');
  if (!number || !unit) return '';
  return `${number}${unit.includes('month') ? 'mo' : unit.charAt(0)}${suffix ? ` ${suffix}` : ''}`;
};

/* 
  Accepts a string denominated in wei and returns a string with a minimal number of digits
*/
export function formatWeiToEthForDisplay({
  weiValue,
  maxDecimals = 4,
  hideCloseToZero = false,
  onError,
}: {
  weiValue: string | bigint | number;
  maxDecimals?: number;
  /**
   * If true and the value is so tiny that it will be converted to scientific notation will return 0 instead of <0.0001
   */
  hideCloseToZero?: boolean;
  onError?: (error: unknown) => void;
}) {
  try {
    const numToFormat = BigInt(weiValue);

    const isZero = numToFormat === 0n;

    if (isZero) {
      return '0';
    }

    const converted = formatEther(numToFormat);

    // else remove trailing zeros by converting to string.
    const clampedToMaxDecimals = (+converted).toFixed(maxDecimals);

    if (hideCloseToZero) {
      return parseFloat(converted) < 0.000001 ? '0' : parseFloat(clampedToMaxDecimals).toString();
    }

    return parseFloat(converted) < 0.0001 ? '<0.0001' : parseFloat(clampedToMaxDecimals).toString();
  } catch (err) {
    // eslint-disable-next-line no-console
    !!onError ? onError(err) : console.error(err);
    return null;
  }
}

export { formatPlayDuration } from '@components-shared/src/helpers/playlist';

/* 
  Logic powering multiselects, eg filter chips
  Includes logic for handling the case where the user clicks all, 
  which should select all options or deselect all options if already selected
*/
export const selectMultiSelectOption = <Keys extends string>({
  prev,
  currentSelected,
  all,
  allKey,
}: {
  /** Previously selected values */
  prev: readonly Keys[];
  /** Currently selected value */
  currentSelected: Keys;
  /** All available values */
  all: readonly Keys[];
  /** Key for all selected values  */
  allKey: Keys;
}) => {
  // if we have `ALL` already selected then we want to unselect it
  if (prev.length >= all.length - 1 && currentSelected === allKey) {
    return [];
  }
  // we selected `ALL` so select everything
  if (currentSelected === allKey) {
    return all.filter(v => v !== allKey);
  }
  // Unselecting a currentSelected
  if (prev.includes(currentSelected)) {
    return prev.filter(g => g !== currentSelected);
  }
  // Selecting a currentSelected
  return [...prev, currentSelected];
};

/* 
  Logic powering multiselects, eg filter chips
  Includes logic for handling the case where no options were selected, and the multiselect falls back to loading all
  which should show the all button as selected
*/
export const multiSelectOptionIsSelected = <Keys extends string>({
  prev,
  currentSelected,
  all,
  allKey,
}: {
  /** Previously selected values */
  prev: readonly Keys[];
  /** Currently selected value */
  currentSelected: Keys;
  /** All available values */
  all: readonly Keys[];
  /** Key for all selected values  */
  allKey: Keys;
}): boolean => {
  // Show all button as active if no or all options are selected
  return currentSelected === allKey && [0, all.length].includes(prev?.length || 0)
    ? true
    : prev?.includes(currentSelected) || false;
};

export const formatProfileHeader = ({ count, text }: { count?: number; text: string | null }) => {
  if (!text) {
    return '';
  }

  if (!count) {
    return `0 ${text}`;
  }

  if (count === 1) {
    return `${count} ${text.slice(0, -1)}`;
  }

  return `${count} ${text}`;
};

export const formatFileTooLargeErrorMessage = ({ message }: { message: string }) => {
  // Message template: 'File is larger than 3145728 bytes'
  const matchesNumber = message.match(/(\d+)/);
  if (matchesNumber && matchesNumber.length > 0) {
    const readableFileSize = byteSize(Number(matchesNumber[0]));
    const updatedErrorMessage = message.replace(/(\d+).*/, readableFileSize.toString());
    // converts error message to: 'File is larger than 3.1 MB'
    return updatedErrorMessage;
  }
  return message;
};

export const doesAddressMatchUsername = ({
  address,
  username,
}: {
  address: string;
  username: string;
}) => {
  return (
    resolveUsernamePublicAddress({ publicAddress: address }).toLowerCase() ===
    username.toLowerCase()
  );
};

export const getFormattedReleaseDate = ({ date }: { date: Date }) => {
  return `${date.toLocaleString('default', {
    month: 'short',
  })} ${date.getDate()}`;
};

export const getFormattedReleaseTime = ({ time }: { time: Date }) => {
  return `${time.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  })}`;
};

export function roundSplitsNum(num: number) {
  return round(num, SPLITS_DECIMALS);
}

export const calculateSplitValueAndRemainder = ({
  formSplitsLength,
}: {
  formSplitsLength: number;
}) => {
  const valueForEach = floor(100 / formSplitsLength, SPLITS_DECIMALS);
  const total = formSplitsLength * valueForEach;

  // If there is a remainder, give it to the logged in user (first address).
  const remainder = roundSplitsNum(100 - total);

  return { valueForEach, remainder };
};

export const calculateUnallocatedSplitTally = ({ splits }: { splits: SplitType[] }) => {
  let splitTally = 0;

  splits.forEach(split => {
    if (split.percent === 0 || !split.percent) {
      splitTally++;
    }
  });

  return splitTally;
};

export const getDraftCoverImageUrl = ({ uploadKey }: { uploadKey: string }) => {
  const coverImageUrl = getCDNUrl({ s3FileKey: uploadKey });
  return coverImageUrl;
};

export const getDraftEditButtonUrl = ({ draftId, stepId }: { draftId: string; stepId: number }) => {
  return `/secret/dashboard/upload/single/${draftId}/step/${stepId}` as const;
};

export const getDraftEditButtonUrlV2 = ({
  draftId,
  stepId,
}: {
  draftId: string;
  stepId: number;
}) => {
  return `/dashboard/upload/${draftId}/${stepId}` as const;
};

export function getReleaseEditButtonUrl({ draftId, stepId }: { draftId: string; stepId: number }) {
  return `/dashboard/upload/edit/single/${draftId}/step/${stepId}` as const;
}

export function getUrlToEditFlow({ releaseId, stepId }: { releaseId: string; stepId: number }) {
  return `/dashboard/edit/${releaseId}/${stepId}` as const;
}

// If you want to display animated cover image if supplied, coalesce(animatedCoverImage, coverImage)
export const getAnimatedCoverImage = ({
  coverImage,
  animatedCoverImage,
}: {
  coverImage: string;
  animatedCoverImage: string | undefined;
}) => {
  return animatedCoverImage ?? coverImage;
};

export const stopPropagationAndForwardClick =
  (fn: (e: React.MouseEvent<HTMLElement>) => void) => (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
    fn(e);
  };

// If you always want to display a static image, coalesce(staticCoverImage, coverImage)
export const getStaticCoverImage = ({
  coverImage,
  staticCoverImage,
}: {
  coverImage: string;
  staticCoverImage: string | undefined;
}) => {
  return staticCoverImage ?? coverImage;
};

// If you want to display animated golden egg image if supplied, coalesce(animatedCoverImage, coverImage)
export const getAnimatedGoldenEggImage = ({
  goldenEggImage,
  animatedGoldenEggImage,
}: {
  goldenEggImage: string;
  animatedGoldenEggImage: string | undefined;
}) => {
  return animatedGoldenEggImage ?? goldenEggImage;
};

export const getUploadPreviewUrl = ({ draftId, stepId }: { draftId: string; stepId: number }) => {
  return `${getDraftEditButtonUrl({ draftId, stepId })}/preview` as const;
};

export function getSingularOrPlural<T extends string>({
  word,
  count,
}: {
  // Word you want to either be singular or plural - only works with words that its plural form is just an additional s at the end
  word: T;
  // Deciding number wether to return the word in a singular or plural form
  count: number;
}) {
  if (count === 0) {
    return `${word}s` as const;
  }
  return count > 1 ? (`${word}s` as const) : word;
}

export const formatWeiToEthDisplayFree = ({
  weiValue,
  maxDecimals = 4,
  hideCloseToZero = false,
  onError,
}: {
  weiValue: string | bigint | number;
  maxDecimals?: number;
  /**
   * If true and the value is so tiny that it will be converted to scientific notation will return 0 instead of <0.0001
   */
  hideCloseToZero?: boolean;
  onError?: (error: unknown) => void;
}) => {
  const formattedEth = formatWeiToEthForDisplay({
    weiValue,
    maxDecimals,
    hideCloseToZero,
    onError,
  });

  if (formattedEth === '0') {
    return 'FREE';
  }

  return `${formattedEth} ETH`;
};

export const getUploadToolStepStatus = ({
  step,
  currentStepId,
  isComplete,
}: {
  step: number;
  currentStepId: number;
  isComplete: boolean;
}) => {
  return step === currentStepId ? 'current' : !!isComplete ? 'completed' : 'disabled';
};

export const MAX_UPLOAD_RELEASE_PRICE_DECIMALS = 4;

// generated with chat gpt lol
export function hasMoreThanXDecimals({
  value,
  maxDecimals,
}: {
  value: number;
  maxDecimals: number;
}): boolean {
  const multiplier = 10 ** maxDecimals;
  const roundedValue = Math.round(value * multiplier);
  const diff = Math.abs(value * multiplier - roundedValue);
  return diff >= Number.EPSILON * multiplier;
}

export const getDistanceString = ({ timestamp }: { timestamp: number }) => {
  const distance = formatDistanceToNowStrict(timestamp, { roundingMethod: 'floor' });
  const [number, unit] = distance.split(' ');
  return `${number}${unit ? unit.charAt(0) : ''}`;
};

export const getNextTrackIds = ({
  trackId,
  releases,
}: {
  trackId: string;
  releases: {
    track: {
      id: string;
    };
  }[];
}) => {
  return () =>
    Array.from(
      releases.reduce(
        (
          acc: {
            lastAccTrackId: string | null;
            trackIds: string[] | null;
          },
          { track: { id: currentTrackId } },
        ) => {
          // We only start adding tracks after the target starting track is found
          if (!acc.trackIds && currentTrackId === trackId) {
            acc.trackIds = [];

            // We filter out the target starting track from being added at the start if it's duplicated there
            acc.lastAccTrackId = trackId;
          }

          /**
           * We don't want contiguouos duplicates
           * and we always start after the target starting track is found
           *
           * We also want to skip logic if we aren't in the process of adding tracks yet
           */
          if (acc.lastAccTrackId === currentTrackId || !acc.trackIds) return acc;

          acc.trackIds.push(currentTrackId);
          acc.lastAccTrackId = currentTrackId;

          return acc;
        },
        {
          lastAccTrackId: null,
          trackIds: null,
        },
      ).trackIds || [],
    );
};

export const isEns = (value?: string) => {
  return value?.endsWith('.eth') || false;
};

export const getCoverOpenGraphImage = ({
  type,
  uploadKey,
}: {
  type: 'ga' | 'golden-egg';
  uploadKey: string;
}) => {
  const seoImage = process.env.NEXT_PUBLIC_OG_SERVICE
    ? `${process.env.NEXT_PUBLIC_OG_SERVICE}/v1/cover/${type}/${encodeURIComponent(uploadKey)}`
    : null;

  return seoImage;
};

export const getReleaseOpenGraphImage = ({ releaseId }: { releaseId: string }) => {
  const seoImage = process.env.NEXT_PUBLIC_OG_SERVICE
    ? `${process.env.NEXT_PUBLIC_OG_SERVICE}/v1/release/${releaseId}`
    : null;

  return seoImage;
};

export function getPostOpenGraphImage({ postId }: { postId: string }) {
  const seoImage = process.env.NEXT_PUBLIC_OG_SERVICE
    ? `${process.env.NEXT_PUBLIC_OG_SERVICE}/v1/post/${postId}`
    : null;

  return seoImage;
}

export const getUserOpenGraphImage = ({ userId }: { userId: string }) => {
  const seoImage = process.env.NEXT_PUBLIC_OG_SERVICE
    ? `${process.env.NEXT_PUBLIC_OG_SERVICE}/v1/user/${userId}`
    : null;

  return seoImage;
};

export const getPlaylistOpenGraphImage = ({ playlistId }: { playlistId: string }) => {
  const seoImage = process.env.NEXT_PUBLIC_OG_SERVICE
    ? `${process.env.NEXT_PUBLIC_OG_SERVICE}/v1/playlist/${playlistId}`
    : null;

  return seoImage;
};

export function humanReadableChainName(chainId: number) {
  try {
    switch (getChainName(chainId)) {
      case 'MAINNET':
      case 'GOERLI':
        return 'Ethereum';
      case 'OPTIMISM':
      case 'OPTIMISM_GOERLI':
        return 'Optimism';
      default:
        return 'UNKNOWN';
    }
  } catch (error) {
    if (chainId !== UNKNOWN_CHAIN_ID) {
      Sentry.captureException(error);
    }
    return 'UNKNOWN';
  }
}

export function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function preciseMillify({
  value,
  precision = 4,
}: {
  value: number;
  /**
   * @default 4
   * The number of characters to show before switching to millify
   */
  precision?: number;
}) {
  const valueString = value.toString();

  if (valueString.length > precision) {
    return millify(value);
  }
  return value.toLocaleString('en-US');
}

/**
 * Takes a string and return it Capitalized (removes symbols)
 *
 * Example:
 * ```
 * formatPascalCase("ALL_DROPS") // All Drops
 * formatPascalCase("ALL-DROPS") // All Drops
 * formatPascalCase("ALL$$$$DROPS") // All Drops
 * ```
 */
export function formatPascalCase(value: string) {
  return startCase(value.toLowerCase());
}

export const getTrackUrl = ({
  isAlbum,
  externalUrl,
  webappUri,
}: {
  isAlbum: boolean;
  externalUrl: string | null;
  webappUri: string;
}) => {
  return isAlbum ? externalUrl ?? '/' : webappUriPath(webappUri);
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'auto' });
};

export const getSortOrder = ({ sortType }: { sortType: string | null }) => {
  switch (sortType) {
    case 'DESC':
      return SortOrder.Desc;
    case 'ASC':
      return SortOrder.Asc;
    default:
      return null;
  }
};

export const formatSlotTimestamp = (timestamp: number) => {
  const min = Math.floor(timestamp / 60);
  const sec = Math.floor(timestamp % 60);

  return `${min}:${sec < 10 ? `0${sec}` : sec}`;
};

export const formatSlotToDisplayTimestamp = (slotNumber: number, trackDuration: number) => {
  const slotTimestamp = (slotNumber / SONG_SLOT_MAX_LENGTH) * trackDuration;
  return formatSlotTimestamp(slotTimestamp);
};

/**
 * A song has only limited slots. We want to encourage users to pick a slot that is not taken.
 * If all slots are taken, we want to allow users to pick a slot that is already taken.
 */
export function canOverwriteSlot({
  totalSlots,
  claimedSlots,
}: {
  /** Number of total available timeslots */
  totalSlots: number;
  /** Number of claimed timeslots */
  claimedSlots: number;
}): boolean {
  return claimedSlots >= totalSlots;
}

/**
 * Focus the html element matching the ID
 */
export function focusHtmlElement(id: string) {
  const element = document.getElementById(id);

  if (element) {
    element.focus();
  }
}

export function getPostInputId(postId: string) {
  return `post-comment-${postId}`;
}
